<template>
  <!-- How it works in five layouts: numbered, timeline (as they were), journey (intro left, steps down a line right),
       rail (a bar and a big number per step) and arrows (columns joined by arrows). Each step's marker is its
       number, nothing, or an iconoir icon (a step without an icon shows its number). -->
  <SectionFrame v-if="steps.length || inStudio" :section="section" :index="index" :heading="layout === 'journey' ? '' : heading" label="How it works">
    <div class="s2-how"><div :class="layout === 'journey' ? 's2-journey' : ''">
      <header v-if="layout === 'journey'" class="s2-journey__intro">
        <component :is="headingTag" v-if="heading" class="t-h2 mo-reveal">{{ heading }}</component>
        <p v-if="intro" class="t-lead">{{ intro }}</p>
        <a v-if="cta" class="t-btn s2-steps-cta" :href="back(href)">{{ cta }}</a>
      </header>
      <p v-else-if="intro" class="t-lead s2-steps-intro">{{ intro }}</p>

      <ol v-if="steps.length" :class="listClass" class="mo-stagger" :style="{'--s2-n': Math.min(steps.length, 4)}">
        <li v-for="(s, i) in steps" :key="i" :class="layout === 'numbered' ? 't-card' : ''">
          <template v-if="layout === 'rail'">
            <span v-if="marker !== 'empty'" class="s2-howrail-n" aria-hidden="true">
              <IconoirIcon v-if="marker === 'icon'" :icon="s.icon">{{ pad(i) }}</IconoirIcon>
              <template v-else>{{ pad(i) }}</template>
            </span>
          </template>
          <span v-else class="s2-step-n" :class="{'is-empty': marker === 'empty'}" aria-hidden="true">
            <IconoirIcon v-if="marker === 'icon'" :icon="s.icon">{{ i + 1 }}</IconoirIcon>
            <template v-else-if="marker === 'number'">{{ i + 1 }}</template>
          </span>
          <div><h3 class="t-h3">{{ s.title }}</h3><p v-if="s.text" class="t-body s2-step-text">{{ s.text }}</p></div>
        </li>
      </ol>
      <StudioGap v-else>Add three to five steps: what happens from first call to finished.</StudioGap>

      <a v-if="cta && layout !== 'journey'" class="t-btn s2-steps-cta" :href="back(href)">{{ cta }}</a>
    </div></div>
  </SectionFrame>
</template>

<script setup>
import {computed} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import IconoirIcon from "@/components/IconoirIcon.vue";
import {layoutOf, markerOf, stepItems} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}});
const inStudio = computed(() => studioMode.value !== "off");
// The first one is the default, so a section saved before the new layouts renders as it did.
const layout = computed(() => layoutOf(props.section, ["numbered", "timeline", "journey", "rail", "arrows"]));
const marker = computed(() => markerOf(props.section));
const heading = computed(() => props.section.heading ?? "How it works");
const steps = computed(() => stepItems(props.section));
const intro = computed(() => String(props.section.description ?? "").trim());
const cta = computed(() => String(props.section.callToAction ?? "").trim());
const href = computed(() => {
  const n = String(props.section.navigateTo || "");
  if (/^(\/|https?:\/\/|tel:|mailto:|#)/i.test(n)) return n;
  return props.section.scrollTarget ? `#${props.section.scrollTarget}` : "/contact";
});
const listClass = computed(() => ({timeline: "s2-timeline", journey: "s2-timeline s2-journey__steps", rail: "s2-steps s2-howrail", arrows: "s2-steps s2-arrows"}[layout.value] || "s2-steps"));
const pad = (i) => String(i + 1).padStart(2, "0");
</script>

<style>
/* The layouts stack by the section's own width (a container query), not the viewport's: the studio preview
   is a narrow pane on a desktop viewport. The @media copies are for browsers without container queries. */
.s2-how { container: s2how / inline-size; }
.s2-steps { list-style: none; padding: 0; margin: 0; display: grid; gap: var(--t-gap); grid-template-columns: repeat(var(--s2-n), minmax(0, 1fr)); }
.s2-steps li { display: flex; flex-direction: column; gap: .75rem; min-width: 0; overflow-wrap: anywhere; }
.s2-step-n { display: inline-flex; width: 2.6rem; height: 2.6rem; flex: none; border-radius: 50%; align-items: center; justify-content: center;
  background: var(--t-accent); color: var(--t-accent-ink); font: var(--t-display-weight) 1.15rem var(--t-font-display); }
/* No glyph: a plain dot, kept in the same footprint so the text lines up. */
.s2-step-n.is-empty { width: 1rem; height: 1rem; margin: .8rem 0; }
.s2-step-text { margin: .35rem 0 0; color: var(--t-muted); }
.s2-steps-intro { margin: 0 0 clamp(1.25rem, 3vw, 2rem); max-width: 60ch; }
.s2-steps-cta { margin-top: 1.75rem; }
.s2-timeline { list-style: none; padding: 0; margin: 0; position: relative; max-width: 760px; }
.s2-timeline::before { content: ""; position: absolute; left: 1.3rem; top: .5rem; bottom: .5rem; width: 2px; background: var(--t-line); }
.s2-timeline li { display: flex; gap: 1.25rem; position: relative; padding: 0 0 1.75rem; min-width: 0; }
.s2-timeline .s2-step-n.is-empty { margin: .8rem .8rem 0; }

/* Journey: the intro and button on the left, the steps down a line on the right. The line runs from one
   marker's centre to the next (1.3rem is half a marker), so it stops at the last marker. */
.s2-journey { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr); gap: clamp(1.5rem, 4vw, 4rem); align-items: start; }
.s2-journey__intro .t-h2 { margin: 0; }
.s2-journey__intro .t-lead { max-width: 44ch; }
.s2-journey__steps { max-width: none; }
.s2-journey__steps::before { display: none; }
.s2-journey__steps li:not(:last-child)::before { content: ""; position: absolute; left: calc(1.3rem - 1px); top: 1.3rem; bottom: -1.3rem; width: 2px; background: var(--t-line); }
.s2-journey__steps .s2-step-n { position: relative; z-index: 1; }
.s2-journey__steps li:last-child { padding-bottom: 0; }

/* Progress rail: a thin accent bar over each step, then a big number. */
.s2-howrail li { border-top: 3px solid var(--t-accent); padding-top: 1rem; gap: .5rem; }
.s2-howrail-n { display: flex; align-items: center; flex: none; min-height: 3.2rem; color: var(--t-accent); font: var(--t-display-weight) clamp(2.2rem, 4vw, 3.2rem) / 1 var(--t-font-display); }
.s2-howrail-n .s2-glyph { width: 2.4rem; height: 2.4rem; }

/* Arrows: columns with the marker on top and an arrow in the gap; the arrow points down when stacked. */
.s2-arrows li { position: relative; }
.s2-arrows li:not(:last-child)::after {
  content: "\2192"; content: "\2192" / ""; position: absolute; top: 1.3rem; right: calc(var(--t-gap) / -2); transform: translate(50%, -50%);
  color: var(--t-accent); font: 700 1.4rem / 1 var(--t-font-body);
}

@container s2how (max-width: 760px) {
  .s2-steps { grid-template-columns: minmax(0, 1fr); }
  .s2-journey { grid-template-columns: minmax(0, 1fr); }
  .s2-arrows li:not(:last-child) { padding-bottom: calc(var(--t-gap) / 2); }
  .s2-arrows li:not(:last-child)::after { content: "\2193"; content: "\2193" / ""; top: auto; right: auto; left: 1.3rem; bottom: calc(var(--t-gap) / -2); transform: translate(-50%, 50%); }
}
@supports not (container-type: inline-size) {
  @media (max-width: 860px) {
    .s2-steps { grid-template-columns: minmax(0, 1fr); }
    .s2-journey { grid-template-columns: minmax(0, 1fr); }
    .s2-arrows li:not(:last-child) { padding-bottom: calc(var(--t-gap) / 2); }
    .s2-arrows li:not(:last-child)::after { content: "\2193"; content: "\2193" / ""; top: auto; right: auto; left: 1.3rem; bottom: calc(var(--t-gap) / -2); transform: translate(-50%, 50%); }
  }
}
</style>
